/* Deskon - the caption travel shared by the homepage sections that lay text over a photograph:
   the "feature panels" pair and the "statement" banner.

   THIS FILE DOES NOT ANIMATE ANYTHING ON A MODERN BROWSER. It measures, and publishes two numbers
   per section. The movement is a scroll-driven CSS animation in assets/deskon-caption-travel.css,
   run by the compositor.

     --dk-travel-cap   how far the caption is lifted before its section has arrived
     --dk-travel-end   how far short of the section's bottom edge the travel finishes

   WHY A SCROLL-DRIVEN ANIMATION AND NOT A LISTENER. Driving a transform from a scroll listener
   means: the page scrolls, the event fires, JavaScript reads the position and writes the
   transform, then the frame is painted. Under momentum scrolling, or whenever the main thread is
   busy, that write lands a frame or more after the scroll it describes - the caption trails the
   page and springs back into place. The arithmetic was never wrong; the loop itself is the
   wobble. A scroll-driven animation has no loop: the scroll position IS the timeline.

   The per-frame path below is kept for browsers without animation-timeline. It is a fallback, not
   the main road - do not move logic back into it.

   ---------------------------------------------------------------------------------------------
   WHAT THE CAPTION FOLLOWS
   ---------------------------------------------------------------------------------------------
   While it is lifted the caption keeps its bottom edge exactly MARGIN above the bottom of the
   WINDOW, so the photograph scrolls up behind it and it reads as descending inside the image. It
   settles onto its resting place at the instant that resting place reaches the same line.

   THE FIRST VERSION ENDED THE TRAVEL WHEN THE SECTION WAS FULLY IN VIEW. For a caption resting at
   the BOTTOM of its box those are the same instant, so the feature panels looked right. For one
   resting in the MIDDLE they are half a box apart: by the time the section had finished entering,
   its middle was already at the middle of the screen, so the caption held a mid-screen position
   for the whole travel and the bottom edge of the window never swept across it.

   MARGIN USED TO BE AN ACCIDENT. The gap under the caption was whatever bottom padding the host
   happened to carry - 40px on a panel, 24px on the banner - so the same effect sat differently in
   the two sections and the banner's button came close to the edge of the screen. It is now one
   stated number and the host's padding is subtracted back out, which is the (1 - ratio) term's
   neighbour in the line below.

     end = hostPaddingBottom + (1 - ratio) x free - MARGIN

   For a bottom-aligned caption in a box padded by MARGIN that comes to zero, which is why the
   feature panels do not move by a pixel.

   TWO SECTIONS, TWO SHAPES, ONE RULE. Rather than special-case them, the room above the caption is
   read off the layout: the free space times 1 for a bottom-aligned box and times 0.5 for a centred
   one, which is what justify-content already says. start is the fraction of that room the caption
   begins at. A section aligned to the top has no room above and simply does not move.

   THE CAPTION IS MEASURED FROM ITS CHILDREN, NOT FROM ITS CONTAINER. base.css gives
   .layout-panel-flex height: 100%, so the container's height is the height of the photograph.
   Measuring it would make the free space negative, the cap zero, and the effect silent. */
(function () {
  'use strict';

  /* host = the element that owns the timeline; content = the flex box holding the caption;
     start = where the caption begins, as a fraction of the room above its resting place (1 would
     tuck it against the top of the box). Add a section by adding a line.

     startMobile IS OPTIONAL AND EXISTS FOR ONE REASON. On a phone the homepage stacks the two
     feature panels and this banner into one column of three identical blocks, and three things
     read one after another get compared: a caption that begins its slide from a different height
     than the two above it shows up as a fault, where on a desktop - side by side, and never in
     the same glance - the same difference reads as variety. So below 750px the banner borrows the
     panels' fraction and the three slides become one gesture.

     THE OTHER HALF OF THAT MATCH IS IN assets/deskon-statement.css, which pads .hero__container
     rather than the content wrapper so that `free` and `end` below come out identical to a
     panel's. BOTH HALVES ARE NEEDED: the same fraction of a different box is still a different
     distance. */
  var TARGETS = [
    {
      /* Both rows of panels: the original and the editor duplicate below it (see the note at
         the top of assets/deskon-feature-panels.css). */
      host: '[id$="__feature_panels"] .group-block, [id$="__section_LypecG"] .group-block',
      content: '.group-block-content',
      start: 0.7
    },
    {
      host: '[id$="__statement"] .hero__container',
      content: '.hero__content-wrapper',
      start: 0.85,
      startMobile: 0.7
    }
  ];

  /* The theme's own breakpoint - the one the hero's mobile media and the stacking rules use. */
  var mobileQuery = window.matchMedia('(max-width: 749px)');

  /* The gap kept between the caption and the bottom of the window while it travels. One number for
     every section, so the effect reads the same wherever it is used. */
  var MARGIN = 40;

  var items = [];
  var queued = false;

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  var composited = !!(window.CSS && CSS.supports && CSS.supports('animation-timeline', 'view()'));

  function collect() {
    items = [];

    for (var t = 0; t < TARGETS.length; t++) {
      var hosts = document.querySelectorAll(TARGETS[t].host);
      for (var i = 0; i < hosts.length; i++) {
        var content = hosts[i].querySelector(TARGETS[t].content);
        if (content) {
          items.push({
            host: hosts[i],
            content: content,
            /* The target, not its start value: which fraction applies depends on the viewport,
               and measure() runs again on every resize. Freezing the number here would leave a
               phone rotated into landscape sliding by the portrait figure. */
            target: TARGETS[t],
            max: 0,
            end: 0
          });
        }
      }
    }

    measure();
    if (!composited) update();
  }

  /* The union of the visible children's boxes. A disabled block, or a line the stylesheet hides,
     reports a zero box and is skipped - otherwise it would drag the union to 0. A translation
     does not change a box's height, so this is safe to re-run mid-travel. */
  function captionHeight(content) {
    var top = Infinity;
    var bottom = -Infinity;
    var kids = content.children;

    for (var i = 0; i < kids.length; i++) {
      var rect = kids[i].getBoundingClientRect();
      if (rect.height === 0 && rect.width === 0) continue;
      if (rect.top < top) top = rect.top;
      if (rect.bottom > bottom) bottom = rect.bottom;
    }

    return bottom > top ? bottom - top : 0;
  }

  function startFraction(target) {
    if (mobileQuery.matches && typeof target.startMobile === 'number') return target.startMobile;
    return target.start;
  }

  function roomRatio(justify) {
    if (justify === 'flex-end' || justify === 'end' || justify === 'last baseline') return 1;
    if (justify === 'center') return 0.5;
    return 0;
  }

  /* Read from the live boxes, never from the editor's numbers: a panel is a window tall on a
     desktop and about half of one on a phone, and a title gains a line when it wraps. */
  function measure() {
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var styles = window.getComputedStyle(it.content);
      var padTop = parseFloat(styles.paddingTop) || 0;
      var padBottom = parseFloat(styles.paddingBottom) || 0;
      var hostPadBottom = parseFloat(window.getComputedStyle(it.host).paddingBottom) || 0;

      var free = it.content.clientHeight - padTop - padBottom - captionHeight(it.content);
      if (free < 0) free = 0;

      var ratio = roomRatio(styles.justifyContent);

      it.max = free * ratio * startFraction(it.target);
      it.end = hostPadBottom + (1 - ratio) * free - MARGIN;

      /* Published on the HOST so both the keyframes and the animation-range on the caption's
         elements can read them by inheritance. */
      it.host.style.setProperty('--dk-travel-cap', it.max.toFixed(1) + 'px');
      it.host.style.setProperty('--dk-travel-end', it.end.toFixed(1) + 'px');
    }
  }

  /* FALLBACK ONLY. shift = how far the caption's resting bottom still is below the line it travels
     along, capped. Far below, the cap applies and the caption waits at its starting height; at or
     above, the floor applies and the caption is at rest.

     Measuring from the top of the window instead would only be the same number while a section is
     exactly one window tall AND its caption rests at the bottom AND its padding happens to equal
     MARGIN - true of the desktop feature panels, false of everything else. */
  function update() {
    queued = false;

    var reduced = motionQuery.matches;
    var viewport = window.innerHeight || document.documentElement.clientHeight;

    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var shift = 0;

      if (!reduced && it.max > 0) {
        var remaining = it.host.getBoundingClientRect().bottom - it.end - viewport;
        shift = Math.min(Math.max(remaining, 0), it.max);
      }

      it.content.style.setProperty('--dk-travel-shift', (-shift).toFixed(1) + 'px');
    }
  }

  function onScroll() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(update);
  }

  function onResize() {
    measure();
    if (!composited) onScroll();
  }

  function start() {
    collect();

    window.addEventListener('resize', onResize);

    if (!composited) {
      window.addEventListener('scroll', onScroll, { passive: true });
      if (motionQuery.addEventListener) motionQuery.addEventListener('change', onScroll);
    }

    /* A web font arriving late changes the caption's height, and with it both numbers. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(onResize);
    }

    /* The theme editor re-renders a section in place; the nodes referenced above are then gone. */
    document.addEventListener('shopify:section:load', collect);
    document.addEventListener('shopify:section:unload', collect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
